import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import adminRequest from 'utils/adminRequest'

import {
  SectionWrapper,
  CardRow,
  TopHeader,
  PaddedDiv,
  TopSection,
  EntityTypeContainer,
  EntityTypeLabel,
  EntityTypeOptions,
  EntityTypeOption,
} from './Style/style'
import InfoCardsUpdate from '../../components/InfoCardsUpdate'
import { COLORS, ANTD_COLORS } from 'theme'
import Button from 'components/Button'
import PlusIcon from 'components/icons/PlusIcon'
import AssignSubscription from './Partials/AssignSubscription'
import SubscribedCollegeTable from './Partials/SubscribedCollegeTable'
import SubscriptionRenewalTable from './Partials/SubscriptionRenewalTable'
import ActiveAssessmentTable from './Partials/ActiveAssessmentTable'
import CompletedAssessmentTable from './Partials/CompletedAssessmentTable'
import RecentActivity from './Partials/RecentActivity'
import GraduationCap from '../../components/icons/GraduationCap'
import BarChart from '../../components/icons/BarChart'
import StackedSquares from '../../components/icons/StackedSquares'
import Refresh from '../../components/icons/Refresh'
import CreateAssessment from './Partials/CreateAssessment'
import AssessmentDetails from './Partials/AssessmentDetails'
import ActiveCollegeList from './Partials/ActiveCollegeList'
import ActiveCorporateList from './Partials/ActiveCorporateList'
import InstituteAssessmentDashboard from './Partials/ActiveCollegeList/InstituteAssessmentDashboard'
import CorporateAssessmentDashboard from './Partials/ActiveCorporateList/CorporateAssessmentDashboard'
import DiagnosisList from './Partials/ActiveCollegeList/DiagnosisList'
import InstituteAssessmentDetails from './Partials/ActiveCollegeList/InstituteAssessmentDetails'
import { selectRoleBasedQuestions, selectHinglishQuestions } from './selectors';
import { useNavigate } from 'react-router-dom'
import { getSetGroupsPendingCount } from './services/assessmentSetGroupAPI'
import { Badge, Modal, message } from 'antd'
import bulkAddStudentsToAssessment from 'utils/bulkAddStudentsToAssessment'
import EnhancedBulkUploadDrawer from './Partials/CreateAssessment/Drawers/EnhancedBulkUploadDrawer'

/**
 * Strangler-fig, at action level rather than module level: this listing screen
 * stays on v1, but "Create Assessments" hands the user to admin-react-v2's
 * entity picker. See modules/Nav/navItems.js for the module-level switch and
 * the full rationale — the same rules apply here. In short: env-gated, not
 * branch-gated, defaulting to the legacy inline wizard, because an environment
 * without the nginx `location /v2` block answers /v2/* with the v1 SPA (HTTP
 * 200 + PageNotFound), which no health check would catch.
 *
 * v2 lives at the same origin under /v2, so this must be a real browser
 * navigation — react-router can't client-route into another app.
 */
const V2_CREATE_ASSESSMENT = process.env.ADMIN_V2_CREATE_ASSESSMENT === '1'

const AssessmentPage = ({
  subscribedInstitutes,
  subscriptionRenewal,
  loading,
  assessmentInfo,
  deliveryFunnel,

  totalCollegeCount,
  activeAssessmentCount,
  assessmentCompletedCount,
  subscribedInstitutesMetrics,
  subscriptionRenewalMetrics,
  subscribedInstitutesPagination,
  subscriptionRenewalPagination,
  filters,
  subscribedCount,
  renewalCount,
  subscribedInstitutesStates,
  subscribedInstitutesCities,
  subscribedInstitutesNames,
  subscribedAssessments,
  fetchSubscribedInstitutes,
  fetchSubscriptionRenewalInstitutes,
  fetchTotalCollegeCount,
  fetchActiveAssessmentCount,
  fetchAssessmentSentCount,
  fetchAssessmentCompletedCount,
  fetchInstitutesStates,
  fetchInstitutesCitiesByState,
  fetchInstitutesByCity,
  fetchAssessmentsByEntity,
  corporateStates,
  corporateCities,
  corporateCompanies,
  fetchCorporateStates,
  fetchCorporateCities,
  fetchCorporateCompanies,
  fetchRecentActivityLogs,
  createAssessments,
  stateCityPairs,
  statesCityPairsRenewal,
  stateCityPairsActive,
  stateCityPairsCompleted,
  activeAssessments,
  activeAssessmentsPagination,
  activeAssessmentsMetrics,
  fetchActiveAssessments,
  completedAssessments,
  completedAssessmentsPagination,
  completedAssessmentsMetrics,
  fetchCompletedAssessments,
  assessmentFilters,
  instituteDomain,
  instituteDegree,
  instituteDepartment,
  instituteSpecialization,
  setAssessmentFilters,
  setInstituteDomain,
  setInstituteDegree,
  setInstituteDepartment,
  setInstituteSpecialization,
  fetchStudentsByStatus,
  studentDataSent,
  studentDataPending,
  studentDataInProgress,
  studentDataLoading,
  studentDataDroppedOff,
  studentDataCompleted,
  fetchAllStudentStatusCounts,
  exportStudentData,
  sendRemindersToStudents,
  resendInvitesToStudents,
  fetchAptitudeTopics,
  scheduleAssessments,
  createCustomSectionQuestions,
  specificAssessmentStudentList,
  fetchSpecificAssessmentStudentList,

}) => {
  const [activeTable, setActiveTable] = useState(null)
  const [showCreateAssessment, setShowCreateAssessment] = useState(false)
  const [showAssignSubscription, setShowAssignSubscription] = useState(false)
  const [showAssessmentDetails, setShowAssessmentDetails] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [entityType, setEntityType] = useState('college')
  const roleBasedQuestionsState = useSelector(selectRoleBasedQuestions);
  const hinglishQuestionsState = useSelector(selectHinglishQuestions);

  // New state for institute/corporate dashboard navigation
  const [selectedInstitute, setSelectedInstitute] = useState(null)
  const [selectedCorporate, setSelectedCorporate] = useState(null)
  const [diagnosisView, setDiagnosisView] = useState(null) // { instituteId, instituteName, assessmentType, assessmentNumber }
  const [instituteAssessmentDetails, setInstituteAssessmentDetails] = useState(null) // { assessment, instituteId, passingYear }
  const [corporateSubscribedCount, setCorporateSubscribedCount] = useState(0)
  const [reviewPendingCount, setReviewPendingCount] = useState(0)
  const routerNavigate = useNavigate()

  // Fetch pending review count for badge
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const res = await getSetGroupsPendingCount()
        setReviewPendingCount(res.data?.data?.count || 0)
      } catch (err) {
        console.error('Failed to fetch pending count:', err)
      }
    }
    fetchPendingCount()
  }, [])

  const handleAssessmentClick = assessment => {
    console.log('Assessment clicked:', assessment)
    setSelectedAssessment(assessment)
    setShowAssessmentDetails(true)
  }

  // --- Add Candidate from the global Active Assessments list ---------------
  // Reuses the exact same drawer + backend endpoint as the per-institute /
  // per-corporate dashboards. The backend (addStudentsToExistingAssessment)
  // routes the invite by origin on its own (scheduled → schedule flow,
  // one-time institute → activation, corporate → OTP or portal per the map's
  // is_otp_invite flag), so the frontend only passes the map id + entityType.
  const [addCandidateDrawerVisible, setAddCandidateDrawerVisible] = useState(false)
  const [selectedAssessmentForAdd, setSelectedAssessmentForAdd] = useState(null)
  const [addCandidateLoading, setAddCandidateLoading] = useState(false)

  const openAddCandidateDrawer = record => {
    setSelectedAssessmentForAdd(record)
    setAddCandidateDrawerVisible(true)
  }

  /* Pull the Active Assessments list back in step, staying on the current page.
     Shared by the post-add refresh and the drawer's Extend end date action —
     extending rewrites the row's dates, so leaving the list stale would show an
     end date the assessment no longer has. */
  const refreshActiveAssessments = () => {
    fetchActiveAssessments({
      ...filters,
      entityType,
      pageNo: activeAssessmentsPagination.page,
      pageLimit: activeAssessmentsPagination.limit,
    })
  }

  const handleActiveAddCandidate = record => {
    // Only scheduled (college) assessments get an informational dialog first.
    if (record?.entityType === 'college' && record?.scheduleId) {
      Modal.confirm({
        title: 'Add candidate to a scheduled assessment',
        content:
          'This assessment is part of a schedule. New candidates will be added to the schedule roster and invited following the schedule flow. Continue?',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: () => openAddCandidateDrawer(record),
      })
      return
    }
    openAddCandidateDrawer(record)
  }

  const handleActiveAddCandidateSubmit = async candidates => {
    if (!candidates || candidates.length === 0) {
      message.warning('No candidates to add')
      return
    }
    const target = selectedAssessmentForAdd
    const isCorporate = target?.entityType === 'corporate'
    setAddCandidateLoading(true)
    const progressKey = 'active-add-candidates-progress'
    try {
      const bulkUploadData = candidates.map(c => ({
        first_name: c.firstName || c.first_name || '',
        last_name: c.lastName || c.last_name || '',
        email: c.email || c.primary_email || '',
        primary_email: c.email || c.primary_email || '',
        // Optional mobile for SMS OTP — the drawer captures it as `phone`.
        // Whitelisted explicitly: this payload is built key-by-key, so anything
        // not listed here never reaches admin-node and the candidate silently
        // gets an email-only OTP.
        mobile_number: c.phone || c.mobile_number || c.mobile || '',
        // Country code travels as its own field so the backend never has to
        // guess whether mobile_number already contains one.
        country_code: c.countryCode || c.country_code || '',
        ...(!isCorporate && (c.degreeObj || c.degree)
          ? {
              degree: c.degreeObj || c.degree,
              department: c.departmentObj || c.department,
              specialization: c.specializationObj || c.specialization,
              passingYear: c.passingYear,
            }
          : {}),
      }))

      const basePayload = isCorporate
        ? {
            entityType: 'corporate',
            assessmentCorporateMapId: target?.assessmentCorporateMapId || target?.id,
          }
        : {
            entityType: 'college',
            // Backend resolves scheduled vs one-time from the map itself.
            assessmentInstituteMapId: target?.assessmentInstituteMapId || target?.id,
          }

      const result = await bulkAddStudentsToAssessment({
        client: adminRequest,
        url: '/assessment/addStudentsToAssessment',
        basePayload,
        bulkUploadData,
        chunkSize: 20,
        onProgress: ({ chunkIndex, totalChunks, addedSoFar, done }) => {
          if (done) {
            message.destroy(progressKey)
            return
          }
          message.loading({
            content: `Adding candidates… batch ${Math.min(chunkIndex + 1, totalChunks)} of ${totalChunks} (${addedSoFar} added)`,
            key: progressKey,
            duration: 0,
          })
        },
      })

      const { addedCount, skippedCount, failedCount, chunkErrors } = result
      if (failedCount === 0) {
        message.success(
          `Added ${addedCount} candidate(s)${skippedCount > 0 ? `, ${skippedCount} duplicate(s) skipped` : ''}`
        )
      } else {
        const firstErr = chunkErrors[0]?.message || 'unknown error'
        message.warning(
          `Added ${addedCount}, skipped ${skippedCount}, ${failedCount} failed (${firstErr}). Re-upload the failed rows to retry.`
        )
      }

      setAddCandidateDrawerVisible(false)
      setSelectedAssessmentForAdd(null)
      refreshActiveAssessments()
    } catch (error) {
      message.destroy(progressKey)
      console.error('Error adding candidates:', error)
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to add candidates'
      )
    } finally {
      setAddCandidateLoading(false)
    }
  }

  // Handle assessment click from institute/corporate dashboard
  const handleDashboardAssessmentClick = assessment => {
    console.log('Dashboard assessment clicked:', assessment)
    // For institute dashboard clicks, show the new InstituteAssessmentDetails (charts + cascading filters)
    if (selectedInstitute) {
      const instituteId = selectedInstitute?.id || selectedInstitute?.instituteCampus?.[0]?.id
      setInstituteAssessmentDetails({
        assessment,
        instituteId,
        passingYear: assessment.passingYear || '',
      })
      return
    }
    // For corporate dashboard clicks, use the existing AssessmentDetails
    const normalizedAssessment = {
      ...assessment,
      id: assessment.assessmentCorporateMapId || assessment.id,
    }
    setSelectedAssessment(normalizedAssessment)
    setShowAssessmentDetails(true)
  }

  // Handle diagnosis click from institute dashboard
  const handleDiagnosisClick = diagnosisInfo => {
    console.log('Diagnosis clicked:', diagnosisInfo)
    setDiagnosisView(diagnosisInfo)
    setInstituteAssessmentDetails(null)
  }

  const handleNavSwitchToDiagnosis = (assessment) => {
    const instituteId = selectedInstitute?.id || selectedInstitute?.instituteCampus?.[0]?.id
    setDiagnosisView({
      instituteId,
      assessmentType: assessment.assessmentType,
      passingYear: assessment.passingYear || '',
      scheduleId: assessment.scheduleId,
      scheduleName: assessment.scheduleName,
      enabledSections: assessment.enabledSections || [],
      assessmentDetails: assessment.assessmentDetails,
    })
    setInstituteAssessmentDetails(null)
  }

  const handleNavSwitchToAssessment = (assessment, detail) => {
    const instituteId = selectedInstitute?.id || selectedInstitute?.instituteCampus?.[0]?.id
    const assessmentDetails = assessment.assessmentDetails || []
    setInstituteAssessmentDetails({
      assessment: {
        ...assessment,
        ...detail,
        id: detail.assessmentInstituteMapId,
        assessmentInstituteMapId: detail.assessmentInstituteMapId,
        assessmentDetails,
      },
      instituteId,
      passingYear: assessment.passingYear || '',
    })
    setDiagnosisView(null)
  }


  // Debounce function
  const debounce = (fn, delay) => {
    let timer
    return function () {
      let context = this,
        args = arguments
      clearTimeout(timer)
      timer = setTimeout(() => {
        fn.apply(context, args)
      }, delay)
    }
  }

  // Create debounced versions of the fetch functions
  const debouncedFetchSubscribed = useCallback(
    debounce(params => {
      fetchSubscribedInstitutes(params)
    }, 500),
    []
  )

  const debouncedFetchRenewal = useCallback(
    debounce(params => {
      fetchSubscriptionRenewalInstitutes(params)
    }, 500),
    []
  )

  /* Callers pass the live entityType in params. Re-applying the captured one
     here let a debounced call queued before a tab switch fire with the old tab,
     and the list thunks only keep the newest request (latestRequest.js). */
  const debouncedFetchActiveAssessments = useCallback(
    debounce(params => {
      fetchActiveAssessments(params)
    }, 500),
    []
  )

  const debouncedFetchCompletedAssessments = useCallback(
    debounce(params => {
      fetchCompletedAssessments(params)
    }, 500),
    []
  )

  // Fetch static data on mount
  useEffect(() => {
    fetchTotalCollegeCount()
    fetchActiveAssessmentCount()
    fetchAssessmentSentCount()
    fetchAssessmentCompletedCount()
    fetchRecentActivityLogs()
    fetchSubscribedInstitutes({})
    fetchSubscriptionRenewalInstitutes({})
    fetchActiveAssessments({})
    fetchCompletedAssessments({})
  }, [])

  // Fetch corporate subscribed count when switching to corporate
  useEffect(() => {
    if (entityType === 'corporate') {
      adminRequest.get('/assessment/getSubscribedCorporates', { params: { pageLimit: 1, pageNo: 0 } })
        .then(res => setCorporateSubscribedCount(res?.data?.data?.count || 0))
        .catch(() => setCorporateSubscribedCount(0))
    }
  }, [entityType])

  useEffect(() => {
    fetchTotalCollegeCount(entityType)
    fetchActiveAssessmentCount(entityType)
    fetchAssessmentSentCount(entityType)
    fetchAssessmentCompletedCount(entityType)

    if (activeTable === 'active-assessment') {
      fetchActiveAssessments({ entityType })
    } else if (activeTable === 'completed-assessment') {
      fetchCompletedAssessments({ entityType })
    }
  }, [entityType])

  // Card data — reordered based on entityType
  // When college: "Active Sub Colleges" first
  // When corporate: "Active Sub Companies" first
  const subscribedCard = {
    icon: <BarChart />,
    text:
      entityType === 'college'
        ? 'Active Sub Colleges'
        : 'Active Sub Companies',
    value: entityType === 'corporate' ? corporateSubscribedCount : subscribedCount,
    background: COLORS.WHITE,
    padding: '10px 20px',
    borderColor:
      activeTable === 'subscribed' ? ANTD_COLORS.PRIMARY : ANTD_COLORS.BORDER,
    isSelected: activeTable === 'subscribed',
    onClick: () =>
      setActiveTable(activeTable === 'subscribed' ? null : 'subscribed'),
  }

  const activeAssessmentCard = {
    icon: <StackedSquares />,
    text:
      entityType === 'college'
        ? 'Active College Assessment'
        : 'Active Corporate Assessment',
    value: activeAssessmentCount,
    background: COLORS.WHITE,
    padding: '10px 20px',
    borderColor:
      activeTable === 'active-assessment'
        ? ANTD_COLORS.PRIMARY
        : ANTD_COLORS.BORDER,
    isSelected: activeTable === 'active-assessment',
    onClick: () =>
      setActiveTable(
        activeTable === 'active-assessment' ? null : 'active-assessment'
      ),
  }

  const completedAssessmentCard = {
    icon: <StackedSquares />,
    text:
      entityType === 'college'
        ? 'Completed College Assessment'
        : 'Completed Corporate Assessment',
    value: assessmentCompletedCount,
    background: COLORS.WHITE,
    padding: '10px 20px',
    borderColor:
      activeTable === 'completed-assessment'
        ? ANTD_COLORS.PRIMARY
        : ANTD_COLORS.BORDER,
    isSelected: activeTable === 'completed-assessment',
    onClick: () =>
      setActiveTable(
        activeTable === 'completed-assessment' ? null : 'completed-assessment'
      ),
  }

  const renewalCard = {
    icon: <Refresh />,
    text:
      entityType === 'college'
        ? 'Colleges to be Renewed / Expired'
        : 'Companies to be Renewed / Expired',
    value: renewalCount,
    background: COLORS.WHITE,
    padding: '10px 20px',
    borderColor:
      activeTable === 'renewal' ? ANTD_COLORS.PRIMARY : ANTD_COLORS.BORDER,
    isSelected: activeTable === 'renewal',
    onClick: () =>
      setActiveTable(activeTable === 'renewal' ? null : 'renewal'),
  }

  // Reordered: subscribed card last
  const cardData = [activeAssessmentCard, completedAssessmentCard, renewalCard, subscribedCard]

  // Determine what to render based on navigation state
  const isInDashboardFlow = selectedInstitute || selectedCorporate || diagnosisView

  return (
    <SectionWrapper column>
      {/* Institute Assessment Details (charts + cascading filters) */}
      {instituteAssessmentDetails ? (
        <InstituteAssessmentDetails
          assessment={instituteAssessmentDetails.assessment}
          instituteId={instituteAssessmentDetails.instituteId}
          passingYear={instituteAssessmentDetails.passingYear}
          onClose={() => setInstituteAssessmentDetails(null)}
          onSwitchToDiagnosis={() => handleNavSwitchToDiagnosis(instituteAssessmentDetails.assessment)}
        />
      ) : /* Diagnosis Student List View */
      diagnosisView && !showAssessmentDetails ? (
        <>
          <div style={{ marginBottom: 16, padding: '0 12px' }}>
            <button
              onClick={() => setDiagnosisView(null)}
              style={{
                background: 'none',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '6px 16px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ← Back to Assessments
            </button>
          </div>
          <DiagnosisList
            scheduleId={diagnosisView.scheduleId}
            latestAssessmentInstituteMapId={diagnosisView.latestAssessmentInstituteMapId}
            assessmentType={diagnosisView.assessmentType}
            enabledSections={diagnosisView.enabledSections}
            scheduleName={diagnosisView.scheduleName}
            passingYear={diagnosisView.passingYear}
            instituteId={diagnosisView.instituteId}
            assessmentDetails={diagnosisView.assessmentDetails}
            onSwitchToAssessment={(detail) => handleNavSwitchToAssessment(diagnosisView, detail)}
          />
        </>
      ) : /* Institute Assessment Dashboard */
      selectedInstitute && !showAssessmentDetails && !diagnosisView ? (
        <InstituteAssessmentDashboard
          institute={selectedInstitute}
          onBack={() => setSelectedInstitute(null)}
          onAssessmentClick={handleDashboardAssessmentClick}
          onDiagnosisClick={handleDiagnosisClick}
        />
      ) : /* Corporate Assessment Dashboard */
      selectedCorporate ? (
        <CorporateAssessmentDashboard
          corporate={selectedCorporate}
          onBack={() => setSelectedCorporate(null)}
        />
      ) : /* Assessment Details View (existing flow) */
      showAssessmentDetails ? (
        <AssessmentDetails
          /* A float's parts stay openable in their own right: this swaps the
             screen to the part's own assessment, which is a normal one. */
          onOpenPart={part => setSelectedAssessment({
            ...selectedAssessment,
            id: part.mapId,
            assessmentName: part.name,
            assessmentType: part.typeName,
            mixMatchGroupId: null,
            parts: [],
          })}
          assessment={selectedAssessment}
          assessmentInfo={assessmentInfo}
          deliveryFunnel={deliveryFunnel}
          onClose={() => {
            setShowAssessmentDetails(false)
            setSelectedAssessment(null)
          }}
          loading={loading}
          assessmentFilters={assessmentFilters}
          instituteDomain={instituteDomain}
          instituteDegree={instituteDegree}
          instituteDepartment={instituteDepartment}
          instituteSpecialization={instituteSpecialization}
          setAssessmentFilters={setAssessmentFilters}
          setInstituteDomain={setInstituteDomain}
          setInstituteDegree={setInstituteDegree}
          setInstituteDepartment={setInstituteDepartment}
          setInstituteSpecialization={setInstituteSpecialization}
          fetchStudentsByStatus={fetchStudentsByStatus}
          fetchAllStudentStatusCounts={fetchAllStudentStatusCounts}
          studentDataSent={studentDataSent}
          studentDataPending={studentDataPending}
          studentDataInProgress={studentDataInProgress}
          studentDataDroppedOff={studentDataDroppedOff}
          studentDataCompleted={studentDataCompleted}
          studentDataLoading={studentDataLoading}
          exportStudentData={exportStudentData}
          sendRemindersToStudents={sendRemindersToStudents}
          resendInvitesToStudents={resendInvitesToStudents}
          entityType={entityType}
          specificAssessmentStudentList={specificAssessmentStudentList}
          fetchSpecificAssessmentStudentList={fetchSpecificAssessmentStudentList}
        />
      ) : /* Create Assessment View */
      showCreateAssessment ? (
        <CreateAssessment
          states={subscribedInstitutesStates}
          cities={subscribedInstitutesCities}
          institutes={subscribedInstitutesNames}
          assessments={subscribedAssessments}
          corporateStates={corporateStates}
          corporateCities={corporateCities}
          corporateCompanies={corporateCompanies}
          loading={loading}
          onClose={() => setShowCreateAssessment(false)}
          fetchStates={fetchInstitutesStates}
          fetchCities={fetchInstitutesCitiesByState}
          fetchInstitutes={fetchInstitutesByCity}
          fetchAssessments={fetchAssessmentsByEntity}
          fetchCorporateStates={fetchCorporateStates}
          fetchCorporateCities={fetchCorporateCities}
          fetchCorporateCompanies={fetchCorporateCompanies}
          createAssessments={createAssessments}
          fetchAptitudeTopics={fetchAptitudeTopics}
          scheduleAssessments={scheduleAssessments}
          roleBasedQuestionsState={roleBasedQuestionsState}
          createCustomSectionQuestions={createCustomSectionQuestions}
        />
      ) : /* Assign Subscription View */
      showAssignSubscription ? (
        <AssignSubscription
          loading={loading}
          onClose={() => setShowAssignSubscription(false)}
        />
      ) : (
        /* Main Dashboard View */
        <>
          <TopSection>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <TopHeader>Assessments</TopHeader>

              {/* Enhanced Entity Type Selector */}
              <EntityTypeContainer>
                <EntityTypeLabel>Entity Type:</EntityTypeLabel>
                <EntityTypeOptions>
                  <EntityTypeOption
                    isActive={entityType === 'college'}
                    onClick={() => setEntityType('college')}
                  >
                    College
                  </EntityTypeOption>
                  <EntityTypeOption
                    isActive={entityType === 'corporate'}
                    onClick={() => setEntityType('corporate')}
                  >
                    Corporate
                  </EntityTypeOption>
                </EntityTypeOptions>
              </EntityTypeContainer>
            </div>

            {/* Always show buttons at the top */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Badge count={reviewPendingCount} offset={[-5, 0]} size="small">
                <Button.Primary
                  width="200px"
                  text="Manage Assessments"
                  style={{ background: '#FBFAFF', color: '#3249D7', border: '1px solid #E3DEFF' }}
                  onClick={() => routerNavigate('/assessment/reviews')}
                />
              </Badge>
              <Button.Primary
                width="200px"
                text="Create Assessments"
                icon={<PlusIcon width="16px" height="16px" />}
                onClick={() => {
                  if (V2_CREATE_ASSESSMENT) {
                    // Carry the entity tab across so v2 opens its picker
                    // pre-set to College/Corporate as selected here.
                    window.location.href = `/v2/assessment?create=1&type=${entityType}`
                    return
                  }
                  setShowCreateAssessment(true)
                }}
              />
            </div>
          </TopSection>

          <PaddedDiv column>
            <CardRow>
              {cardData.map((card, index) => (
                <InfoCardsUpdate
                  key={index}
                  padding={card.padding}
                  width={'24%'}
                  height={'90px'}
                  icon={card.icon}
                  text={card.text}
                  value={card.value}
                  onClick={card.onClick}
                  background={card.background}
                  borderColor={`1px solid ${card.borderColor}`}
                  isClickable={!!card.onClick}
                  isSelected={card.isSelected}
                  style={{
                    borderRadius: '10px',
                    boxShadow: 'none',
                  }}
                />
              ))}
            </CardRow>
            <br></br>

            {/* Active Sub Colleges / Active Sub Companies — now shows institute/corporate list */}
            {activeTable === 'subscribed' && entityType === 'college' && (
              <ActiveCollegeList
                onSelectInstitute={(institute) => setSelectedInstitute(institute)}
              />
            )}

            {activeTable === 'subscribed' && entityType === 'corporate' && (
              <ActiveCorporateList
                onSelectCorporate={(corporate) => setSelectedCorporate(corporate)}
              />
            )}

            {activeTable === 'active-assessment' && (
              <ActiveAssessmentTable
                metrics={activeAssessmentsMetrics}
                activeAssessments={activeAssessments}
                loading={loading}
                onAssessmentClick={handleAssessmentClick}
                stateCityPairs={stateCityPairsActive}
                filters={filters}
                page={activeAssessmentsPagination.page}
                limit={activeAssessmentsPagination.limit}
                totalItems={activeAssessmentsPagination.totalItems}
                currentCount={activeAssessmentsPagination.currentCount}
                totalPages={activeAssessmentsPagination.totalPages}
                entityType={entityType}
                onAddCandidate={handleActiveAddCandidate}
                onPageChange={page => {
                  fetchActiveAssessments({
                    ...filters,
                    pageNo: page,
                    pageLimit: activeAssessmentsPagination.limit,
                    pageOnly: true,
                    type_name: '',
                    entityType: entityType,
                  })
                }}
                onFilterChange={newFilters => {
                  const updatedFilters = {
                    ...filters,
                    ...newFilters,
                    states: newFilters.states || filters.states,
                    cities: newFilters.cities || filters.cities,
                    pageNo: 0,
                    pageLimit: activeAssessmentsPagination.limit,
                    updateFilters: true,
                    entityType: entityType,
                  }

                  debouncedFetchActiveAssessments(updatedFilters)
                }}
              />
            )}

            {/* Add Candidate drawer for the global Active Assessments list */}
            {addCandidateDrawerVisible && (
              <EnhancedBulkUploadDrawer
                visible={addCandidateDrawerVisible}
                onClose={() => {
                  setAddCandidateDrawerVisible(false)
                  setSelectedAssessmentForAdd(null)
                }}
                assessmentData={
                  selectedAssessmentForAdd?.entityType === 'corporate'
                    ? {
                        entityId: selectedAssessmentForAdd?.entityId,
                        // Drives the "closes in N days" gate and its Extend
                        // action. Same id the add itself posts, so the date the
                        // admin settles is the one the candidates land on.
                        assessmentEndDate: selectedAssessmentForAdd?.endDate,
                        assessmentStartDate: selectedAssessmentForAdd?.startDate,
                        assessmentCorporateMapId:
                          selectedAssessmentForAdd?.assessmentCorporateMapId ||
                          selectedAssessmentForAdd?.id,
                        onEndDateUpdated: refreshActiveAssessments,
                        preservedCandidates: [],
                        preservedUploadSummaries: [],
                        timestamp: Date.now(),
                      }
                    : {
                        qualificationData: [],
                        instituteId: selectedAssessmentForAdd?.instituteId,
                        instituteCampusId: selectedAssessmentForAdd?.instituteCampusId,
                        /* One-time college rows only. A scheduled row is one
                           generated RUN of a schedule — its dates come from the
                           schedule's frequency and validity and the next run
                           would overwrite anything set here, so its end date is
                           edited on the schedule itself. Passing no date simply
                           hides the gate. `scheduleId` is the reliable signal in
                           THIS table: handleActiveAddCandidate already branches
                           on it for the scheduled-assessment dialog. */
                        assessmentEndDate: selectedAssessmentForAdd?.scheduleId
                          ? undefined
                          : selectedAssessmentForAdd?.endDate,
                        assessmentStartDate: selectedAssessmentForAdd?.startDate,
                        assessmentInstituteMapId:
                          selectedAssessmentForAdd?.assessmentInstituteMapId ||
                          selectedAssessmentForAdd?.id,
                        onEndDateUpdated: refreshActiveAssessments,
                        preservedCandidates: [],
                        preservedUploadSummaries: [],
                        timestamp: Date.now(),
                      }
                }
                entityType={
                  selectedAssessmentForAdd?.entityType === 'corporate'
                    ? 'corporate'
                    : 'college'
                }
                mode="addCandidate"
                onDataSubmit={candidates => {
                  handleActiveAddCandidateSubmit(candidates)
                }}
              />
            )}

            {activeTable === 'completed-assessment' && (
              <CompletedAssessmentTable
                metrics={completedAssessmentsMetrics}
                completedAssessments={completedAssessments}
                loading={loading}
                onAssessmentClick={handleAssessmentClick}
                stateCityPairs={stateCityPairsCompleted}
                filters={filters}
                page={completedAssessmentsPagination.page}
                limit={completedAssessmentsPagination.limit}
                totalItems={completedAssessmentsPagination.totalItems}
                currentCount={completedAssessmentsPagination.currentCount}
                totalPages={completedAssessmentsPagination.totalPages}
                entityType={entityType}
                onPageChange={page => {
                  fetchCompletedAssessments({
                    ...filters,
                    pageNo: page,
                    pageLimit: completedAssessmentsPagination.limit,
                    pageOnly: true,
                    type_name: '',
                    entityType: entityType,
                  })
                }}
                onFilterChange={newFilters => {
                  const updatedFilters = {
                    ...filters,
                    ...newFilters,
                    states: newFilters.states || filters.states,
                    cities: newFilters.cities || filters.cities,
                    pageNo: 0,
                    pageLimit: completedAssessmentsPagination.limit,
                    updateFilters: true,
                    entityType: entityType,
                  }

                  debouncedFetchCompletedAssessments(updatedFilters)
                }}
              />
            )}

            {activeTable === 'renewal' && (
              <SubscriptionRenewalTable
                instituteList={subscriptionRenewal}
                page={subscriptionRenewalPagination.page}
                limit={subscriptionRenewalPagination.limit}
                totalItems={subscriptionRenewalPagination.totalItems}
                currentCount={subscriptionRenewalPagination.currentCount}
                loading={loading}
                filters={filters}
                metrics={subscriptionRenewalMetrics}
                stateCityPairs={statesCityPairsRenewal}
                onPageChange={page => {
                  fetchSubscriptionRenewalInstitutes({
                    ...filters,
                    pageNo: page,
                    pageLimit: subscriptionRenewalPagination.limit,
                    pageOnly: true,
                    assessment_type: 'communication',
                  })
                }}
                onFilterChange={newFilters => {
                  const updatedFilters = {
                    ...filters,
                    ...newFilters,
                    states: newFilters.states || filters.states,
                    cities: newFilters.cities || filters.cities,
                    pageNo: 0,
                    pageLimit: subscriptionRenewalPagination.limit,
                    updateFilters: true,
                    assessment_type: 'communication',
                  }

                  debouncedFetchRenewal(updatedFilters)
                }}
              />
            )}

            {activeTable === null && <RecentActivity />}
          </PaddedDiv>
        </>
      )}
    </SectionWrapper>
  )
}

export default AssessmentPage
